import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { MobileNavigation } from '../components/MobileNavigation';
import { MagnifyingGlass, User, CaretUp, CheckCircle, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminPlayers() {
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  
  const loadPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (ageGroupFilter !== 'all') params.append('age_group', ageGroupFilter);
      if (search) params.append('search', search);
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/players?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPlayers(data.players);
        setTotalPlayers(data.total);
        setTotalPages(data.total_pages);
      } else {
        toast.error('Failed to load players');
      }
    } catch (error) {
      console.error('Load players error:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [page, ageGroupFilter, search]);
  
  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadPlayers();
  };
  
  const openOverrideDialog = (player) => {
    setSelectedPlayer(player);
    // Check if player has 12-14 in their approved groups
    setOverrideEnabled(player.approved_age_groups?.includes('12-14') || false);
    setShowOverrideDialog(true);
  };
  
  const handleSaveOverride = async () => {
    if (!selectedPlayer) return;
    
    setSavingOverride(true);
    try {
      // For a 9-11 player, enabling override means approving them for 12-14
      const approvedGroups = overrideEnabled ? ['12-14'] : [];
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/players/${selectedPlayer.player_id}/age-override`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ approved_age_groups: approvedGroups })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowOverrideDialog(false);
        loadPlayers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update age override');
      }
    } catch (error) {
      console.error('Save override error:', error);
      toast.error('Failed to save override');
    } finally {
      setSavingOverride(false);
    }
  };
  
  const canHaveOverride = (player) => {
    // Only 9-11 players can be approved to play up
    return player.natural_age_group === '9-11';
  };
  
  if (loading && players.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-players-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-heading">PLAYER MANAGEMENT</h1>
              <p className="text-sm text-muted-foreground">
                {totalPlayers} total player{totalPlayers !== 1 ? 's' : ''}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadPlayers}
              data-testid="refresh-players-btn"
            >
              <ArrowsClockwise size={16} className="mr-1" />
              Refresh
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  placeholder="Search by player name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="player-search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="search-btn">
                Search
              </Button>
            </form>
            
            <Select value={ageGroupFilter} onValueChange={(v) => { setAgeGroupFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="age-group-filter">
                <SelectValue placeholder="Age Group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ages</SelectItem>
                <SelectItem value="9-11">Ages 9-11</SelectItem>
                <SelectItem value="12-14">Ages 12-14</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Players List */}
      <div className="px-4 py-6 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : players.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <User size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO PLAYERS FOUND</h3>
              <p className="text-muted-foreground">
                {search ? 'Try adjusting your search criteria' : 'No players registered yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {players.map((player) => (
              <Card 
                key={player.player_id} 
                className="bg-card border-border card-hover"
                data-testid={`player-card-${player.player_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-heading text-lg">{player.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-primary/10 rounded-full text-primary">
                          Ages {player.natural_age_group}
                        </span>
                        {player.has_age_override && (
                          <span className="text-xs px-2 py-0.5 bg-green-500/10 rounded-full text-green-500 flex items-center gap-1">
                            <CaretUp size={12} weight="bold" />
                            Can Play Up
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>Age: {player.age} years old</p>
                        {player.parent && (
                          <p>Parent: {player.parent.name} ({player.parent.email})</p>
                        )}
                        {player.approved_age_groups?.length > 0 && (
                          <p className="text-green-400">
                            <CheckCircle size={14} className="inline mr-1" weight="fill" />
                            Approved for: Ages {player.approved_age_groups.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {canHaveOverride(player) ? (
                        <Button
                          variant={player.has_age_override ? "default" : "outline"}
                          size="sm"
                          onClick={() => openOverrideDialog(player)}
                          data-testid={`override-btn-${player.player_id}`}
                        >
                          <CaretUp size={16} className="mr-1" />
                          {player.has_age_override ? 'Override Active' : 'Age Override'}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground px-3 py-2">
                          12-14 players cannot play down
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Age Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">AGE GROUP OVERRIDE</DialogTitle>
            <DialogDescription>
              Allow this player to participate in sessions for a different age group.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlayer && (
            <div className="space-y-6">
              {/* Player Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <p className="font-medium text-lg">{selectedPlayer.name}</p>
                <p className="text-sm text-muted-foreground">
                  Age: {selectedPlayer.age} years old
                </p>
                <p className="text-sm">
                  Natural Age Group: <span className="font-medium">Ages {selectedPlayer.natural_age_group}</span>
                </p>
              </div>
              
              {/* Override Toggle */}
              {canHaveOverride(selectedPlayer) ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="space-y-1">
                      <Label htmlFor="override-toggle" className="font-medium">
                        Allow Play with 12-14 Age Group
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        This player will be able to check in to 12-14 sessions
                      </p>
                    </div>
                    <Switch
                      id="override-toggle"
                      checked={overrideEnabled}
                      onCheckedChange={setOverrideEnabled}
                      data-testid="age-override-toggle"
                    />
                  </div>
                  
                  {overrideEnabled && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm text-green-400 flex items-start gap-2">
                        <CheckCircle size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                        <span>
                          When enabled, {selectedPlayer.name} can check in to both 9-11 AND 12-14 sessions.
                        </span>
                      </p>
                    </div>
                  )}
                  
                  {!overrideEnabled && selectedPlayer.has_age_override && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400 flex items-start gap-2">
                        <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                        <span>
                          Disabling will restrict {selectedPlayer.name} to only 9-11 sessions.
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400 flex items-start gap-2">
                    <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                    <span>
                      Players in the 12-14 age group cannot be approved to play with younger players (9-11) for safety reasons.
                    </span>
                  </p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowOverrideDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                {canHaveOverride(selectedPlayer) && (
                  <Button
                    onClick={handleSaveOverride}
                    disabled={savingOverride}
                    className="flex-1 btn-primary"
                    data-testid="save-override-btn"
                  >
                    {savingOverride ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
